/**
 * MobileBriefDetail — the FULL prebrief digest for one surface, rendered as markdown.
 *
 * CLUSTER-1 (research-visibility uplift). MobileHome previously truncated each prebrief's
 * `digest_markdown` to a single ~160-char line. This screen renders the WHOLE digest via ReactMarkdown
 * (mirroring the desktop MyBriefs render: `prose` + remark-gfm), so a mobile user can finally read the
 * day's synthesis without opening desktop.
 *
 * Route: /partner/m/brief-detail/:surface
 *
 * DATA — the caller's OWN prebrief for this surface, at-or-before today, via the
 * `mobile_brief_detail(_surface, _target_date)` SECURITY DEFINER RPC, which returns
 *   { ok: true,  brief: { surface, target_date, digest_markdown, has_signal, generated_at } }
 *   { ok: false, reason: 'no_brief' }
 * The RPC gates to user_id = auth.uid() (own prebriefs only), so no extra capability check is needed
 * client-side — same gate as the home feed.
 *
 * READ-ONLY and offline-aware, mirroring the sibling mobile pages exactly:
 *   • ONE useCachedQuery (cacheKey scoped per surface) write-throughs the resolved brief as a unit and
 *     the queryFn THROWS on { error } so the cached last-known-good keeps serving offline.
 *   • `{ ok:false, no_brief }` is a valid, non-error state → a calm empty card, never an error banner.
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *
 * The generated supabase types are stale for this RPC, so the call is cast through `any`. No writes.
 * Web/desktop are untouched (this is a /partner/m/* surface).
 */
import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronLeft, FileText } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

interface BriefRow {
  surface: string;
  target_date: string | null;
  digest_markdown: string | null;
  has_signal: boolean | null;
  generated_at: string | null;
}

interface BriefDetailData {
  ok: boolean;
  brief: BriefRow | null;
}

const EMPTY_DETAIL: BriefDetailData = { ok: false, brief: null };

function prettySurface(surface: string | null): string {
  if (!surface) return 'Brief';
  const key = surface.toLowerCase();
  if (key === 'cio') return 'CIO';
  if (key === 'pm') return 'PM';
  return surface
    .split(/[_\-\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

async function fetchBriefDetail(surface: string): Promise<BriefDetailData> {
  const { data, error } = await (supabase.rpc as any)('mobile_brief_detail', { _surface: surface });
  if (error) throw error;
  const raw = (data ?? {}) as { ok?: boolean; brief?: BriefRow | null };
  return { ok: raw.ok === true, brief: raw.brief ?? null };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileBriefDetail() {
  const online = useIsOnline();
  const { surface = '' } = useParams<{ surface: string }>();

  const detail = useCachedQuery<BriefDetailData>({
    queryKey: ['mobile-brief-detail', surface],
    cacheKey: `mobile.brief_detail.${surface}`,
    queryFn: () => fetchBriefDetail(surface),
    enabled: surface.length > 0,
  });

  const data = detail.data ?? EMPTY_DETAIL;
  const brief = data.brief;

  const showStale = detail.isFromCache && detail.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && detail.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(detail.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  const hardError = detail.isError && !detail.isFromCache && detail.data === undefined;

  const dateLabel = useMemo(() => {
    if (!brief?.target_date) return null;
    try {
      return format(new Date(brief.target_date), 'EEE d MMM yyyy');
    } catch {
      return brief.target_date;
    }
  }, [brief?.target_date]);

  const generatedLabel = useMemo(() => {
    if (!brief?.generated_at) return null;
    try {
      return formatDistanceToNow(new Date(brief.generated_at), { addSuffix: true });
    } catch {
      return null;
    }
  }, [brief?.generated_at]);

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <p className="text-sm text-muted-foreground">Daily brief</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">
          {prettySurface(surface || brief?.surface || null)}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {dateLabel && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{dateLabel}</p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load this brief just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved copy of this brief yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : brief && brief.digest_markdown ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="prose prose-sm max-w-none font-body prose-headings:font-heading prose-a:text-primary dark:prose-invert">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{brief.digest_markdown}</ReactMarkdown>
            </div>
            {generatedLabel && (
              <p className="mt-3 border-t border-border/60 pt-2 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                Updated {generatedLabel}
              </p>
            )}
          </section>
        ) : (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <FileText className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">Nothing material today</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              There’s no brief for this surface today — your role-shaped digest will appear here when
              it’s ready.
            </p>
            {!online && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                You’re offline — this will refresh once you reconnect.
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
